import React from 'react';
import { LineChart } from '@mui/x-charts/LineChart';
import { Card, CardContent, Typography, Box, useTheme, useMediaQuery } from '@mui/material';

interface TimeSeriesData {
  period: string;
  claims_received: number;
  locs_sent: number;
  responses_received: number;
  cancelled_claims: number;
}

interface TimeSeriesChartProps {
  data: TimeSeriesData[];
  granularity: string;
  loading?: boolean;
}

const TimeSeriesChart: React.FC<TimeSeriesChartProps> = ({ data, granularity, loading }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  if (loading || !data || data.length === 0) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography color="text.secondary">
              {loading ? 'Loading chart data...' : 'No data available'}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Extract data for each series
  const periods = data.map(item => item.period);
  const claimsReceived = data.map(item => item.claims_received);
  const locsSent = data.map(item => item.locs_sent);
  const responsesReceived = data.map(item => item.responses_received);
  const cancelledClaims = data.map(item => item.cancelled_claims);

  // Format date labels based on granularity
  const formatLabel = (period: string) => {
    const date = new Date(period);
    if (granularity === 'daily') {
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
    } else if (granularity === 'weekly') {
      return `Week ${date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}`;
    } else {
      return date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
    }
  };

  const chartHeight = isMobile ? 300 : isTablet ? 350 : 400;

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Claims Trends ({granularity.charAt(0).toUpperCase() + granularity.slice(1)})
        </Typography>
        <Box sx={{ width: '100%', height: chartHeight }}>
          <LineChart
            series={[
              { 
                data: claimsReceived, 
                label: 'Claims Received',
                color: '#007bff'
              },
              { 
                data: locsSent, 
                label: 'LOCs Sent',
                color: '#28a745'
              },
              { 
                data: responsesReceived, 
                label: 'Responses',
                color: '#6f42c1'
              },
              { 
                data: cancelledClaims, 
                label: 'Cancelled',
                color: '#dc3545'
              },
            ]}
            xAxis={[
              {
                data: periods,
                scaleType: 'point',
                tickLabelStyle: {
                  angle: isMobile ? 45 : 0,
                  textAnchor: isMobile ? 'start' : 'middle',
                },
                valueFormatter: formatLabel,
              },
            ]}
            yAxis={[
              {
                label: 'Count',
                min: 0,
              },
            ]}
            slotProps={{
              legend: {
                direction: isMobile ? 'column' : 'row' as any,
                position: { vertical: 'top', horizontal: 'center' },
              },
            }}
            margin={{ 
              left: 50, 
              right: 20, 
              top: 60, 
              bottom: isMobile ? 80 : 50 
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default TimeSeriesChart;